import React, { useEffect, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import SaveIcon from "@material-ui/icons/Save";
import SendIcon from "@material-ui/icons/Send";
import RefreshIcon from "@material-ui/icons/Refresh";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto"
  },
  header: { marginBottom: theme.spacing(3) },
  card: { padding: theme.spacing(3), marginBottom: theme.spacing(2) },
  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  effectiveStateBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(2)
  },
  effectiveStateOn: {
    background:
      theme.palette.type === "dark" ? "rgba(46, 125, 50, 0.16)" : "#e8f5e9",
    border: "1px solid #2e7d32",
    color: theme.palette.type === "dark" ? "#a5d6a7" : "#1b5e20"
  },
  effectiveStateOff: {
    background:
      theme.palette.type === "dark" ? "rgba(158, 158, 158, 0.16)" : "#f5f5f5",
    border: "1px solid #9e9e9e",
    color: theme.palette.type === "dark" ? "#bdbdbd" : "#424242"
  },
  effectiveStateText: { fontSize: 18, fontWeight: 700, lineHeight: 1.2 },
  effectiveStateSub: { fontSize: 13, opacity: 0.85 },
  statusOn: { backgroundColor: "#2e7d32", color: "white" },
  statusOff: { backgroundColor: "#9e9e9e", color: "white" },
  monoBox: {
    background: theme.palette.type === "dark" ? "#222" : "#f4f4f7",
    padding: theme.spacing(2),
    borderRadius: 6,
    fontFamily: "monospace",
    fontSize: 13,
    whiteSpace: "pre-wrap",
    overflowX: "auto",
    border: `1px solid ${theme.palette.divider}`
  },
  eventChip: { margin: theme.spacing(0.5) },
  statBlock: {
    textAlign: "center",
    padding: theme.spacing(2),
    background: theme.palette.type === "dark" ? "#1d1d1d" : "#fafafa",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6
  },
  statValue: { fontSize: 22, fontWeight: 700 },
  statLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary
  },
  formField: { marginBottom: theme.spacing(2) },
  copyButton: { marginTop: theme.spacing(1) },
  warningBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: theme.palette.type === "dark" ? "#3e2c1a" : "#fff8e1",
    borderRadius: 6,
    borderLeft: `4px solid #f9a825`,
    fontSize: 13
  },
  hint: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    background: theme.palette.type === "dark" ? "#1a2733" : "#e3f2fd",
    borderRadius: 6,
    borderLeft: `4px solid #1976d2`
  },
  actionsRow: { display: "flex", gap: 8, flexWrap: "wrap" }
}));

const SAMPLE_PAYLOAD = JSON.stringify(
  {
    event: "message.received",
    timestamp: "2026-05-25T13:15:00.000Z",
    companyId: 1,
    ticketId: 123,
    contactId: 456,
    whatsappId: 23,
    fromMe: false,
    body: "Olá, qual o preço dos planos?",
    status: "open"
  },
  null,
  2
);

const SAMPLE_INBOUND = JSON.stringify(
  {
    action: "send_message",
    ticketId: 123,
    body: "Olá! Vou te enviar nossos planos agora mesmo.",
    metadata: { source: "n8n" }
  },
  null,
  2
);

const copyToClipboard = (text, label) => {
  try {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  } catch {
    toast.warn("Não foi possível copiar.");
  }
};

const Integrations = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const [form, setForm] = useState({
    enabled: false,
    url: "",
    secret: "",
    timeoutMs: 3000
  });
  const [saving, setSaving] = useState(false);
  const [autoToggled, setAutoToggled] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/api/integrations/n8n/status");
      setStatus(data);
      setForm(prev => ({
        ...prev,
        enabled: !!data?.outbound?.enabled,
        timeoutMs: data?.outbound?.timeout_ms ?? 3000
      }));
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Quando o usuario digita uma URL pela primeira vez e o outbound esta
  // desligado, sugere ligar automaticamente (mais visivel que esquecer).
  const handleUrlChange = e => {
    const value = e.target.value;
    setForm(prev => {
      const next = { ...prev, url: value };
      if (
        !prev.enabled &&
        value.trim().length > 8 &&
        !autoToggled
      ) {
        next.enabled = true;
        setAutoToggled(true);
        toast.info(
          "Switch 'Outbound habilitado' foi ligado automaticamente. Verifique antes de salvar."
        );
      }
      return next;
    });
  };

  const applyServerPatch = data => {
    setStatus(prev =>
      prev
        ? {
            ...prev,
            outbound: {
              ...prev.outbound,
              enabled: !!data?.enabled,
              url_masked: data?.url_masked ?? prev.outbound?.url_masked,
              url_full_length:
                data?.url_full_length ?? prev.outbound?.url_full_length,
              has_secret: !!data?.has_secret,
              timeout_ms: data?.timeout_ms ?? prev.outbound?.timeout_ms,
              source: data?.source ?? prev.outbound?.source
            },
            inbound: {
              ...prev.inbound,
              configured: !!data?.has_secret
            }
          }
        : prev
    );
    setForm(prev => ({
      ...prev,
      url: "",
      secret: "",
      enabled: !!data?.enabled,
      timeoutMs: data?.timeout_ms ?? prev.timeoutMs
    }));
  };

  const doSave = async forceEnable => {
    setSaving(true);
    try {
      const patch = {
        enabled: forceEnable === true ? true : !!form.enabled,
        timeoutMs: Number(form.timeoutMs) || 3000
      };
      if (form.url && form.url.trim()) patch.url = form.url.trim();
      if (form.secret && form.secret.trim()) patch.secret = form.secret.trim();
      const { data } = await api.put(
        "/api/integrations/n8n/config",
        patch
      );
      applyServerPatch(data);
      toast.success(
        patch.enabled
          ? "Configuração salva e Outbound ativo"
          : "Configuração salva (Outbound desabilitado)"
      );
      setAutoToggled(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async e => {
    if (e) e.preventDefault();
    await doSave(false);
  };
  const handleSaveAndEnable = async () => {
    await doSave(true);
  };

  const handleTest = async () => {
    try {
      await api.post("/api/integrations/n8n/test");
      toast.success("Evento test.ping enfileirado");
      setTimeout(fetchStatus, 1500);
    } catch (err) {
      toastError(err);
    }
  };

  const outboundEnabled = !!status?.outbound?.enabled;
  const inboundConfigured = !!status?.inbound?.configured;
  const hasUrlConfigured = !!status?.outbound?.url_masked;
  const queue = status?.queue;

  const showEnableWarning =
    isAdmin &&
    !form.enabled &&
    ((form.url && form.url.trim().length > 8) || hasUrlConfigured);

  return (
    <div className={classes.root}>
      <Container maxWidth="md">
        <Box className={classes.header}>
          <Typography variant="h5">Integrações</Typography>
          <Typography variant="body2" color="textSecondary">
            Central de automação MIT Chat. Conecte o sistema ao n8n para criar
            fluxos automatizados de atendimento.
          </Typography>
        </Box>

        {/* ===== Banner de estado efetivo ===== */}
        {!loadingStatus && (
          <Box
            className={`${classes.effectiveStateBanner} ${
              outboundEnabled
                ? classes.effectiveStateOn
                : classes.effectiveStateOff
            }`}
          >
            <PowerSettingsNewIcon style={{ fontSize: 36 }} />
            <Box style={{ flex: 1 }}>
              <div className={classes.effectiveStateText}>
                Outbound: {outboundEnabled ? "ATIVO" : "DESLIGADO"}
              </div>
              <div className={classes.effectiveStateSub}>
                {outboundEnabled && hasUrlConfigured && (
                  <>
                    Eventos serão enviados para{" "}
                    {status?.outbound?.url_masked}.
                  </>
                )}
                {outboundEnabled && !hasUrlConfigured && (
                  <>Switch ligado, mas nenhuma URL configurada.</>
                )}
                {!outboundEnabled && hasUrlConfigured && (
                  <>
                    URL configurada, mas Switch desligado. Eventos NÃO serão
                    enviados.
                  </>
                )}
                {!outboundEnabled && !hasUrlConfigured && (
                  <>Sem URL e sem ativação. Nada será enviado.</>
                )}
              </div>
            </Box>
          </Box>
        )}

        {/* ===== Status + Queue ===== */}
        <Paper className={classes.card}>
          <Box className={classes.cardTitle}>
            <Typography variant="h6" style={{ flex: 1 }}>
              n8n / Webhooks
            </Typography>
            <Chip
              size="small"
              icon={
                outboundEnabled ? (
                  <CheckCircleIcon style={{ color: "white" }} />
                ) : (
                  <HighlightOffIcon style={{ color: "white" }} />
                )
              }
              label={outboundEnabled ? "Outbound ativo" : "Outbound desabilitado"}
              className={outboundEnabled ? classes.statusOn : classes.statusOff}
            />
            <Chip
              size="small"
              icon={
                inboundConfigured ? (
                  <CheckCircleIcon style={{ color: "white" }} />
                ) : (
                  <HighlightOffIcon style={{ color: "white" }} />
                )
              }
              label={inboundConfigured ? "Inbound protegido" : "Inbound sem secret"}
              className={
                inboundConfigured ? classes.statusOn : classes.statusOff
              }
            />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchStatus}
              disabled={loadingStatus}
            >
              Atualizar
            </Button>
          </Box>

          {loadingStatus && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loadingStatus && status && (
            <>
              <Grid container spacing={2} style={{ marginTop: 8 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    URL configurada
                  </Typography>
                  <Typography style={{ fontFamily: "monospace", fontSize: 13 }}>
                    {status.outbound?.url_masked || "— não configurada —"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">
                    Timeout
                  </Typography>
                  <Typography style={{ fontFamily: "monospace", fontSize: 13 }}>
                    {status.outbound?.timeout_ms ?? "—"} ms
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">
                    Origem
                  </Typography>
                  <Typography style={{ fontFamily: "monospace", fontSize: 13 }}>
                    {status.outbound?.source ?? "—"}
                  </Typography>
                </Grid>
              </Grid>

              <Divider style={{ margin: "24px 0" }} />
              <Typography variant="subtitle2" gutterBottom>
                Status da fila Bull (n8n outbound)
              </Typography>
              {queue && !queue.error ? (
                <Grid container spacing={2}>
                  {[
                    ["waiting", "Aguardando"],
                    ["active", "Em execução"],
                    ["completed", "Concluídos"],
                    ["failed", "Falharam"],
                    ["delayed", "Atrasados"]
                  ].map(([k, label]) => (
                    <Grid item xs={6} sm={2} key={k}>
                      <div className={classes.statBlock}>
                        <div className={classes.statValue}>{queue[k] ?? 0}</div>
                        <div className={classes.statLabel}>{label}</div>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {queue?.error
                    ? `Erro ao ler fila: ${queue.error}`
                    : "Fila indisponível."}
                </Typography>
              )}

              <Divider style={{ margin: "24px 0" }} />
              <Typography variant="subtitle2" gutterBottom>
                Eventos disparados (outbound)
              </Typography>
              <Box>
                {(status.outbound?.events || []).map(ev => (
                  <Chip
                    key={ev}
                    label={ev}
                    size="small"
                    className={classes.eventChip}
                    variant="outlined"
                  />
                ))}
              </Box>

              <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                Ações inbound suportadas
              </Typography>
              <Box>
                {(status.inbound?.actions || []).map(ac => (
                  <Chip
                    key={ac}
                    label={ac}
                    size="small"
                    className={classes.eventChip}
                  />
                ))}
              </Box>
            </>
          )}
        </Paper>

        {/* ===== Edição (admin) ===== */}
        {isAdmin && (
          <Paper className={classes.card}>
            <Typography variant="h6" gutterBottom>
              Configuração
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Valores ficam armazenados na tabela <code>Settings</code> e têm
              prioridade sobre as variáveis do <code>.env</code>. URL e Secret
              aparecem em branco por segurança — só preencha para alterar.
            </Typography>

            <form onSubmit={handleSave} autoComplete="off">
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={(_, v) =>
                      setForm(prev => ({ ...prev, enabled: v }))
                    }
                    color="primary"
                  />
                }
                label={
                  <span>
                    Outbound habilitado{" "}
                    <strong style={{ opacity: 0.7 }}>
                      ({form.enabled ? "LIGADO" : "DESLIGADO"})
                    </strong>
                  </span>
                }
                className={classes.formField}
              />

              <TextField
                fullWidth
                label="URL do Webhook do n8n"
                variant="outlined"
                placeholder="https://n8n.mitsolution.com.br/webhook/..."
                value={form.url}
                onChange={handleUrlChange}
                className={classes.formField}
                helperText="Deixe vazio para manter o valor salvo atualmente"
              />

              <TextField
                fullWidth
                label="Secret"
                type="password"
                variant="outlined"
                value={form.secret}
                onChange={e =>
                  setForm(prev => ({ ...prev, secret: e.target.value }))
                }
                className={classes.formField}
                helperText="Deixe vazio para manter o secret atual"
                inputProps={{ autoComplete: "new-password" }}
              />

              <TextField
                fullWidth
                label="Timeout (ms)"
                type="number"
                variant="outlined"
                value={form.timeoutMs}
                onChange={e =>
                  setForm(prev => ({ ...prev, timeoutMs: e.target.value }))
                }
                inputProps={{ min: 500, max: 30000, step: 500 }}
                className={classes.formField}
              />

              {showEnableWarning && (
                <Box className={classes.warningBox}>
                  ⚠️ Você está salvando com <strong>Outbound desligado</strong>{" "}
                  e URL definida. Os eventos NÃO serão enviados ao n8n. Use{" "}
                  <strong>"Salvar e ativar"</strong> se quiser ligar o switch
                  agora.
                </Box>
              )}

              <Box className={classes.actionsRow} style={{ marginTop: 12 }}>
                <Button
                  type="submit"
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                >
                  {saving ? "Salvando..." : "Salvar configuração"}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  startIcon={<PowerSettingsNewIcon />}
                  onClick={handleSaveAndEnable}
                  disabled={saving}
                >
                  Salvar e ativar
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={handleTest}
                  disabled={!outboundEnabled || !hasUrlConfigured}
                >
                  Testar webhook
                </Button>
              </Box>
              {(!outboundEnabled || !hasUrlConfigured) && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginTop: 8 }}
                >
                  Para testar, é necessário Outbound ATIVO e URL configurada.
                </Typography>
              )}
            </form>
          </Paper>
        )}

        {!isAdmin && (
          <Box className={classes.hint}>
            <Typography variant="body2">
              Apenas usuários administradores podem editar URL, secret e
              habilitar a integração.
            </Typography>
          </Box>
        )}

        {/* ===== Exemplos ===== */}
        <Paper className={classes.card}>
          <Typography variant="h6" gutterBottom>
            Payload — evento outbound
          </Typography>
          <div className={classes.monoBox}>{SAMPLE_PAYLOAD}</div>
          <Button
            size="small"
            startIcon={<FileCopyOutlinedIcon />}
            className={classes.copyButton}
            onClick={() => copyToClipboard(SAMPLE_PAYLOAD, "Payload outbound")}
          >
            Copiar exemplo
          </Button>
        </Paper>

        <Paper className={classes.card}>
          <Typography variant="h6" gutterBottom>
            Payload — ação inbound do n8n
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            HTTP Request POST com header <code>x-mitchat-webhook-secret</code>.
          </Typography>
          <div className={classes.monoBox}>{SAMPLE_INBOUND}</div>
          <Button
            size="small"
            startIcon={<FileCopyOutlinedIcon />}
            className={classes.copyButton}
            onClick={() => copyToClipboard(SAMPLE_INBOUND, "Payload inbound")}
          >
            Copiar exemplo
          </Button>
        </Paper>
      </Container>
    </div>
  );
};

export default Integrations;
